import { useCallback, useEffect, useMemo } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useUpdatePurchasesPermissions } from 'src/components/Purchases/BulkActions/useUpdatePurchasesPermissions';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { BulkCancelPurchaseDialog } from 'src/dialogs/BulkEdits/BulkCancelPurchaseDialog';
import { PurchaseCancellationType } from 'src/dialogs/CancelPurchaseDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditPreviewWithDetails,
  BulkEditPurchaseClient,
  CommandOptionType,
  PurchaseOrderQuery,
} from 'src/WebApiController';

import { PURCHASE_BULK_CANCEL_UPDATE_KEY } from '../../PurchaseActionDropdown.constants';
import { LaunchBulkCancelPurchase } from './LaunchBulkCancelPurchase';

export const useBulkCancelPurchase = (
  filterQuery: PurchaseOrderQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  groupId?: string,
  shouldRegister?: boolean
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasUpdatePurchasesPermission } = useUpdatePurchasesPermissions();

  const { activeAccountWebClientConfig } = useAppContext();

  const updateKey = groupId
    ? `cancelPurchases-event-${groupId}`
    : PURCHASE_BULK_CANCEL_UPDATE_KEY;

  const { showErrorDialog } = useClientLogContext();
  const { refreshData } = usePurchaseDataContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
    refreshCatalog,
  } = useCatalogDataContext();
  const { refreshMetrics } = useCatalogMetricsContext();

  const {
    openDialog: openBulkCancelDialog,
    closeDialog: closeBulkCancelDialog,
  } = useDialog(DialogId.BulkCancelPurchase, BulkCancelPurchaseDialog);

  const onSubmit = useCallback(
    async (
      cancellationType: PurchaseCancellationType | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const client = new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            );

            const preview =
              await client.bulkDeletePurchaseOrderPreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateTagsPreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (cancellationType) {
        await tryInvokeApi(
          async () => {
            const succeeded =
              cancellationType === 'cancel'
                ? await new BulkEditPurchaseClient(
                    activeAccountWebClientConfig
                  ).bulkCancelPurchaseOrder(
                    {
                      item1: preview!.preview,
                      item2: null,
                    },
                    updateKey,
                    supportBackgroundProcess,
                    null
                  )
                : await new BulkEditPurchaseClient(
                    activeAccountWebClientConfig
                  ).bulkVoidPurchaseOrder(
                    preview!.preview,
                    updateKey,
                    supportBackgroundProcess
                  );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                await refreshData(true);
                // Metrics/events may have changed with TGs being cancelled, so refresh the whole catalog
                refreshMetrics();
                await refreshCatalog();
                await refreshExpandedListItems();
              }

              closeBulkCancelDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitCancelPurchase', error, {
              trackErrorData: { preview, cancellationType },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkCancelDialog,
      filterQuery,
      refreshCatalog,
      refreshData,
      refreshExpandedListItems,
      refreshMetrics,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkCancelDialog({
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkCancelDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.PurchaseBulkCancelPurchase,
        onSelect: openDialogStableRef,
      },
      disabled: !hasUpdatePurchasesPermission || !shouldRegister,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.PurchaseBulkCancelPurchase,
        disabled: !hasUpdatePurchasesPermission || !shouldRegister,
      });
    };
  }, [
    hasUpdatePurchasesPermission,
    shouldRegister,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkCancelDialog({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkCancelDialog,
      });
    },
    [
      closeBulkCancelDialog,
      onSubmit,
      openBulkCancelDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasUpdatePurchasesPermission) {
      return [];
    }

    return [
      {
        title: ContentId.CancelPurchasesParenth,
        onClick: onClick,
        disabled: isLoading,
      },
    ];
  }, [hasUpdatePurchasesPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchBulkCancelPurchase
        key="bulkCancelPurchase"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
